import ScheduleIcon from '@mui/icons-material/Schedule';
import type { MyFeedScope } from '@pulse/shared';
import { Box, Button, CustomSnackbar, Grid, PageContainer, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@pulse/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { useAuth } from '../../../context/AuthContext';
import { DependencyFormDialog } from '../../delivery/components/DependencyFormDialog';
import { EscalationFormDialog } from '../../delivery/components/EscalationFormDialog';
import { IssueDetailDialog } from '../../delivery/components/IssueDetailDialog';
import { RiskFormDialog } from '../../delivery/components/RiskFormDialog';
import { useDependencies, useTransitionDependencyAcrossProjects } from '../../delivery/hooks/useDependencies';
import { useEscalations, useTransitionEscalationAcrossProjects } from '../../delivery/hooks/useEscalations';
import { useTransitionIssue, useUpdateIssue } from '../../delivery/hooks/useIssues';
import { useProjects } from '../../delivery/hooks/useProjects';
import { useRisks, useTransitionRiskAcrossProjects } from '../../delivery/hooks/useRisks';
import { useWorkflowDefinition } from '../../delivery/hooks/useWorkflow';
import { HoursLoggedCard } from '../components/HoursLoggedCard';
import { KpiRow } from '../components/KpiRow';
import { LedProjectsCard, type LedProjectRow } from '../components/LedProjectsCard';
import { LogTimeDialog } from '../components/LogTimeDialog';
import { MyWorkPanel } from '../components/MyWorkPanel';
import { WeeklyHoursChart } from '../components/WeeklyHoursChart';
import {
  HOME_KEYS,
  useMyDependencies,
  useMyEscalations,
  useMyIssues,
  useMyLedProjects,
  useMyRisks,
  useMyTimeSummary,
} from '../hooks/useHome';
import {
  dependencyToRow,
  escalationToRow,
  HOME_TAB_CONFIG,
  issueToRow,
  riskToRow,
  type HomeTabKind,
  type HomeWorkRow,
} from '../types';

// The default post-login landing page (AC7) — a personal dashboard of the current user's own
// work across every project, recreated from requirements/ui/home page/README.md using our own
// component set rather than the design handoff's raw HTML/CSS (see the approved plan). Sign-out
// lives in the global UserMenu (apps/web/src/layout/AppLayout.tsx), not duplicated here.
//
// The work panel is tabbed (Tasks / Risks / …). Each tab's API response is adapted into the
// shared HomeWorkRow shape (see ../types.ts) so MyWorkPanel and its rows stay kind-agnostic;
// only this page knows which query and which mutation belong to which tab.
export function HomePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [activeKind, setActiveKind] = useState<HomeTabKind>('issue');
  const config = HOME_TAB_CONFIG[activeKind];
  // Lives here, not in MyWorkPanel, so it isn't wiped by the per-tab remount below — a search
  // term applies across tabs, since it only matches title/project name, which every kind has.
  const [search, setSearch] = useState('');

  // §SA-409 3b/3d — a Project Lead or Project Manager lands on the Lead view and can switch to
  // Individual; everyone else never sees the toggle and stays on their own work. `scopeOverride`
  // is null until they actually pick, so the default falls out of the data rather than needing
  // an effect that re-syncs once the query resolves.
  const { data: ledProjects = [], isSuccess: ledProjectsResolved } = useMyLedProjects();
  const isLeadOrManager = ledProjects.length > 0;
  // §SA-409 3f — Expedite is a PL/PM action, decided per row: in Lead view every row qualifies,
  // and in Individual view a PL/PM keeps it on their own projects but not on ones they merely
  // work on. The API enforces the same rule; this only stops offering a button that would 403.
  const ledProjectIds = useMemo(() => new Set(ledProjects.map((p) => p.id)), [ledProjects]);
  const canExpedite = useCallback((row: HomeWorkRow) => ledProjectIds.has(row.projectId), [ledProjectIds]);
  const [scopeOverride, setScopeOverride] = useState<MyFeedScope | null>(null);
  const scope: MyFeedScope = scopeOverride ?? (isLeadOrManager ? 'led' : 'mine');

  // All four kinds load up front rather than lazily per tab — the KPI row's Open Risks/
  // Dependencies/Escalations cards need every count on first load, and React Query caches the
  // results so switching tabs stays instant. All four are also held back until the scope is
  // known — see the `enabled` note in useHome.ts.
  const { data: issues = [], isLoading: issuesLoading } = useMyIssues(scope, ledProjectsResolved);
  const { data: risks = [], isLoading: risksLoading } = useMyRisks(scope, ledProjectsResolved);
  const { data: dependencies = [], isLoading: dependenciesLoading } = useMyDependencies(scope, ledProjectsResolved);
  const { data: escalations = [], isLoading: escalationsLoading } = useMyEscalations(scope, ledProjectsResolved);
  const { data: timeSummary } = useMyTimeSummary();

  // Status names — and which of them count as "finished" — always come from the DB-configured
  // workflow for the active tab's entity type, never from a literal in this file.
  const { data: workflow } = useWorkflowDefinition(config.workflowEntityType);
  const orderedSteps = useMemo(() => [...(workflow?.steps ?? [])].sort((a, b) => a.order - b.order), [workflow]);
  const statusOptions = useMemo(() => orderedSteps.map((s) => s.name), [orderedSteps]);
  const terminalStatuses = useMemo(() => orderedSteps.filter((s) => s.isTerminal).map((s) => s.name), [orderedSteps]);

  // KPI row's Open Risks/Dependencies/Escalations counts need "not yet resolved" for every kind
  // at once, not just the active tab — so each kind's own workflow is fetched independently of
  // `workflow` above (which only ever tracks the active tab's entity type). React Query dedupes
  // by queryKey, so this never double-fetches whichever kind happens to be active.
  const { data: riskWorkflow } = useWorkflowDefinition('risk');
  const { data: dependencyWorkflow } = useWorkflowDefinition('dependency');
  const { data: escalationWorkflow } = useWorkflowDefinition('escalation');
  const openRisksCount = useMemo(() => {
    const terminal = new Set((riskWorkflow?.steps ?? []).filter((s) => s.isTerminal).map((s) => s.name));
    return risks.filter((r) => !terminal.has(r.status)).length;
  }, [risks, riskWorkflow]);
  const openDependenciesCount = useMemo(() => {
    const terminal = new Set((dependencyWorkflow?.steps ?? []).filter((s) => s.isTerminal).map((s) => s.name));
    return dependencies.filter((d) => !terminal.has(d.status)).length;
  }, [dependencies, dependencyWorkflow]);
  const openEscalationsCount = useMemo(() => {
    const terminal = new Set((escalationWorkflow?.steps ?? []).filter((s) => s.isTerminal).map((s) => s.name));
    return escalations.filter((e) => !terminal.has(e.status)).length;
  }, [escalations, escalationWorkflow]);

  // Full project records for the "My Projects" rail card — the ordinary projects list, narrowed
  // to the ones the caller runs, rather than the Project Insights report: that report needs
  // `reporting:read`, which a Project Lead has no reason to hold, and everything the card shows
  // is available here under `project:read`. Each is paired with its completion/budget figures
  // from useMyLedProjects, so the card makes no requests of its own.
  const { data: allProjects = [] } = useProjects();
  const ledProjectRecords = useMemo((): LedProjectRow[] => {
    const figuresById = new Map(ledProjects.map((p) => [p.id, p]));
    return allProjects.flatMap((project) => {
      const figures = figuresById.get(project.id);
      return figures ? [{ project, figures }] : [];
    });
  }, [allProjects, ledProjects]);

  // The one place that maps a tab to its query. Everything downstream sees HomeWorkRow[] and
  // has no idea which kind it's rendering.
  const { rows, isLoading } = useMemo((): { rows: HomeWorkRow[]; isLoading: boolean } => {
    switch (activeKind) {
      case 'risk':
        return { rows: risks.map(riskToRow), isLoading: risksLoading };
      case 'dependency':
        return { rows: dependencies.map(dependencyToRow), isLoading: dependenciesLoading };
      case 'escalation':
        return { rows: escalations.map(escalationToRow), isLoading: escalationsLoading };
      default:
        return { rows: issues.map(issueToRow), isLoading: issuesLoading };
    }
  }, [
    activeKind,
    issues, issuesLoading,
    risks, risksLoading,
    dependencies, dependenciesLoading,
    escalations, escalationsLoading,
  ]);

  const [logTimeOpen, setLogTimeOpen] = useState(false);
  const [logTimeIssueId, setLogTimeIssueId] = useState<string | undefined>(undefined);
  // Carries the tab to land on, since two different row buttons open this same dialog.
  const [detailIssue, setDetailIssue] = useState<{ projectId: string; issueId: string; tab: 'Comments' | 'Work Log' } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Row whose form-dialog button was clicked (risk/dependency/escalation tabs). Those dialogs
  // want the full IRisk/IDependency/IEscalation, but Home holds only the lean summary — and
  // there's no single-item GET in this API, so we do what RiskLinkPage/EscalationLinkPage
  // already do: fetch that one project's list and pick the row out of it. `''` leaves the query
  // disabled (see the hooks' `enabled: Boolean(projectId)`), so nothing is fetched until a
  // button is actually used.
  const [dialogTarget, setDialogTarget] = useState<HomeWorkRow | null>(null);
  const { data: dialogProjectRisks = [] } = useRisks(dialogTarget?.kind === 'risk' ? dialogTarget.projectId : '');
  const { data: dialogProjectDependencies = [] } = useDependencies(
    dialogTarget?.kind === 'dependency' ? dialogTarget.projectId : '',
  );
  const { data: dialogProjectEscalations = [] } = useEscalations(
    dialogTarget?.kind === 'escalation' ? dialogTarget.projectId : '',
  );
  // Null until that fetch resolves. The dialogs treat a null entity as "create", so they must
  // stay unmounted until the real row is in hand rather than open into an empty create form.
  const dialogRisk =
    dialogTarget?.kind === 'risk' ? dialogProjectRisks.find((r) => r.id === dialogTarget.id) ?? null : null;
  const dialogDependency =
    dialogTarget?.kind === 'dependency'
      ? dialogProjectDependencies.find((d) => d.id === dialogTarget.id) ?? null
      : null;
  const dialogEscalation =
    dialogTarget?.kind === 'escalation' ? dialogProjectEscalations.find((e) => e.id === dialogTarget.id) ?? null : null;

  const updateIssueMutation = useUpdateIssue();
  const transitionIssueMutation = useTransitionIssue();
  const transitionRiskMutation = useTransitionRiskAcrossProjects();
  const transitionDependencyMutation = useTransitionDependencyAcrossProjects();
  const transitionEscalationMutation = useTransitionEscalationAcrossProjects();

  function openLogTime(row?: HomeWorkRow) {
    // §SA-409 3g — in Lead view the same icon reads the issue's Work Log instead of opening the
    // personal log-time dialog: a lead reviews their team's entries here, they don't log hours
    // against someone else's task (which is also why Log Time itself is disabled in this view).
    if (scope === 'led' && row) {
      setDetailIssue({ projectId: row.projectId, issueId: row.id, tab: 'Work Log' });
      return;
    }
    setLogTimeIssueId(row?.id);
    setLogTimeOpen(true);
  }

  function openComments(row: HomeWorkRow) {
    setDetailIssue({ projectId: row.projectId, issueId: row.id, tab: 'Comments' });
  }

  // MyWorkPanel only renders this button on tabs whose config sets a `formDialogAction`, so
  // `row` here is always a risk, dependency or escalation — never an issue.
  function openFormDialog(row: HomeWorkRow) {
    setDialogTarget(row);
  }

  // The dialogs invalidate their own project-scoped keys (['risks', projectId]) on save, which
  // Home doesn't read — so the matching /me list has to be invalidated here too, or an edited
  // title or status would go stale in the list sitting behind the dialog.
  function handleDialogSaved(message: string, queryKey: readonly string[]) {
    setErrorMessage(null);
    setSuccessMessage(message);
    void queryClient.invalidateQueries({ queryKey });
  }

  // Only issues carry an `expedited` flag; MyWorkPanel never renders the toggle without one.
  function handleToggleExpedited(row: HomeWorkRow) {
    if (!row.issue) return;
    updateIssueMutation.mutate(
      { projectId: row.projectId, issueId: row.id, data: { expedited: !row.issue.expedited } },
      {
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: HOME_KEYS.issues }),
        onError: (err: Error) => setErrorMessage(err.message),
      },
    );
  }

  // Each kind transitions through its own project-scoped endpoint, and each of those is
  // permission-gated on the row's own project (`risk:write` etc.) — so an owner who lacks the
  // permission there gets the API's 403 message in the snackbar rather than a silent no-op.
  function handleStatusChange(row: HomeWorkRow, toStep: string) {
    const onError = (err: Error) => setErrorMessage(err.message);
    const invalidate = (queryKey: readonly string[]) => () => void queryClient.invalidateQueries({ queryKey });

    switch (row.kind) {
      case 'risk':
        transitionRiskMutation.mutate(
          { projectId: row.projectId, riskId: row.id, toStep },
          { onSuccess: invalidate(HOME_KEYS.risks), onError },
        );
        return;
      case 'dependency':
        transitionDependencyMutation.mutate(
          { projectId: row.projectId, dependencyId: row.id, toStep },
          { onSuccess: invalidate(HOME_KEYS.dependencies), onError },
        );
        return;
      case 'escalation':
        transitionEscalationMutation.mutate(
          { projectId: row.projectId, escalationId: row.id, toStep },
          { onSuccess: invalidate(HOME_KEYS.escalations), onError },
        );
        return;
      default:
        // An issue's status is what the My Projects card's completion % is computed from, so
        // those figures are refreshed along with the issue list.
        transitionIssueMutation.mutate(
          { projectId: row.projectId, issueId: row.id, toStep },
          {
            onSuccess: () => {
              invalidate(HOME_KEYS.issues)();
              invalidate(HOME_KEYS.ledProjects)();
            },
            onError,
          },
        );
    }
  }

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const dailyTargetHours = timeSummary?.dailyTargetHours ?? 8;

  return (
    <PageContainer maxWidth="xl">
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5}>
          <Box>
            <Typography variant="h4" fontWeight={700}>Welcome back, {user?.name ?? 'there'}</Typography>
            <Typography variant="body2" color="text.secondary">{today}</Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' } }}>
            {/* §SA-409 3d — only a Project Lead or Project Manager gets the choice; everyone
                else never sees it and stays on their own work. */}
            {isLeadOrManager && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={scope}
                onChange={(_e, next: MyFeedScope | null) => next && setScopeOverride(next)}
                aria-label="Home view"
                // MUI's ToggleButton defaults (uppercase, 4px radius, its own grey selected
                // state) don't match this app's Button, which theme.ts sets to textTransform
                // none + 8px radius. Restated here rather than in the theme so the existing
                // Burn Rate toggles aren't restyled as a side effect.
                sx={{
                  borderRadius: 2,
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    px: 1.5,
                    py: 0.5,
                  },
                  '& .MuiToggleButton-root.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                }}
              >
                <ToggleButton value="led">Lead</ToggleButton>
                <ToggleButton value="mine">Individual</ToggleButton>
              </ToggleButtonGroup>
            )}
            {/* §SA-409 3e — a work log always records against the caller, so logging from the
                Lead view (which lists the whole team's work) would book a teammate's hours to
                you. Switching to Individual is what unlocks it. */}
            <Tooltip title={scope === 'led' ? 'Switch to Individual to log your time' : ''}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<ScheduleIcon />}
                  onClick={() => openLogTime()}
                  disabled={scope === 'led'}
                >
                  Log Time
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Issue-only cards (Expedited/Overdue/Due Today) stay bound to `issues` regardless of
            the active tab; the Open Risks/Dependencies/Escalations cards are computed above
            from each kind's own /me list and workflow. */}
        <KpiRow
          issues={issues}
          openRisksCount={openRisksCount}
          openDependenciesCount={openDependenciesCount}
          openEscalationsCount={openEscalationsCount}
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            {/* Remounting per tab resets the chip/status/grouping selections, which are
                kind-specific — a Risk status can't match an Issue, so carrying them over would
                silently hide every row. `search` is deliberately hoisted out of that remount. */}
            <MyWorkPanel
              key={`${scope}-${activeKind}`}
              config={config}
              scope={scope}
              onTabChange={setActiveKind}
              rows={rows}
              search={search}
              onSearchChange={setSearch}
              statusOptions={statusOptions}
              terminalStatuses={terminalStatuses}
              onToggleExpedited={handleToggleExpedited}
              canExpedite={canExpedite}
              onStatusChange={handleStatusChange}
              onLogTime={openLogTime}
              onOpenComments={openComments}
              onOpenFormDialog={openFormDialog}
            />
            {/* `ledProjectsResolved` guards a flash of the empty state: until it's true the
                four feeds are disabled, and a disabled v5 query reports isLoading === false
                (isPending && isFetching) while rows is still [] — which would otherwise render
                "Nothing assigned to you yet" for a beat on every page load. */}
            {ledProjectsResolved && !isLoading && rows.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                {config.emptyStateText}
              </Typography>
            )}
          </Grid>

          {/* position: relative is the containing block LedProjectsCard pins itself to from md up,
              so the work panel alone sets this row's height — see that component for why. */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ position: 'relative' }}>
            {/* Lead view swaps the personal time-tracking rail for the projects the caller runs
                — a lead can't log time in this view (§SA-409 3e), so the hours widgets have
                nothing to say here. */}
            {scope === 'led' ? (
              <LedProjectsCard projects={ledProjectRecords} />
            ) : (
              <Stack spacing={2}>
                <Paper sx={{ p: 2, borderRadius: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>This Week</Typography>
                  <WeeklyHoursChart days={timeSummary?.days ?? []} dailyTargetHours={dailyTargetHours} />
                </Paper>
                <HoursLoggedCard timeSummary={timeSummary} />
              </Stack>
            )}
          </Grid>
        </Grid>
      </Stack>

      <LogTimeDialog
        open={logTimeOpen}
        onClose={() => setLogTimeOpen(false)}
        issues={issues}
        defaultIssueId={logTimeIssueId}
      />

      {detailIssue && (
        <IssueDetailDialog
          projectId={detailIssue.projectId}
          issueId={detailIssue.issueId}
          initialTab={detailIssue.tab}
          onClose={() => setDetailIssue(null)}
        />
      )}

      {/* Both dialogs own their own save/delete mutations and report back through
          onSaved/onError, same as ProjectRisksTab wires them — this page stays out of it and
          only owns the snackbar. onEscalated is deliberately not passed: the Risk->Escalation
          prompt belongs to the project's Risks tab, not to a cross-project Home list. */}
      {dialogRisk && (
        <RiskFormDialog
          projectId={dialogRisk.projectId}
          open
          onClose={() => setDialogTarget(null)}
          risk={dialogRisk}
          onSaved={(msg) => handleDialogSaved(msg, HOME_KEYS.risks)}
          onError={setErrorMessage}
        />
      )}

      {dialogDependency && (
        <DependencyFormDialog
          projectId={dialogDependency.projectId}
          open
          onClose={() => setDialogTarget(null)}
          dependency={dialogDependency}
          onSaved={(msg) => handleDialogSaved(msg, HOME_KEYS.dependencies)}
          onError={setErrorMessage}
        />
      )}

      {dialogEscalation && (
        <EscalationFormDialog
          projectId={dialogEscalation.projectId}
          open
          onClose={() => setDialogTarget(null)}
          escalation={dialogEscalation}
          onSaved={(msg) => handleDialogSaved(msg, HOME_KEYS.escalations)}
          onError={setErrorMessage}
        />
      )}

      <CustomSnackbar
        open={errorMessage !== null || successMessage !== null}
        onClose={() => { setErrorMessage(null); setSuccessMessage(null); }}
        message={errorMessage ?? successMessage}
        severity={errorMessage !== null ? 'error' : 'success'}
        autoHideDuration={4000}
      />
    </PageContainer>
  );
}
